import React, { FC } from 'react';
import { Redirect, Route, Switch } from 'react-router-dom';
import Dashboard from './pages/Dashboard/Dashboard';
import { DeliveryComponent } from './pages/delivery/DeliveryComponent';
import NewOrder from './pages/new-order/NewOrder';
import ShiftPlanner from './pages/new-shift-planner/ShiftPlanner';
import JourneysPage from './pages/order/journey/JourneysPage';
import OrderComponent from './pages/order/OrderComponent';
import Orders from './pages/orders/Orders';
import OrdersSearch from './pages/orders/OrdersSearch';
import { Planner } from './pages/planner/Planner';
import PlannerPrintList from 'pages/oldPlanner/planner-print-list/PrintList';
import { ShiftForCourier } from './pages/shift/ShiftForCourier';
import { Region } from './types/orderTypes';
import PlannerMap from './pages/oldPlanner/PlannerMap';
import Routes from './pages/routes/Routes';
import Monitor, { MonitorView } from './pages/bundles/Monitor';
import { RoleType } from './types/userTypes';
import { ScanPerformanceLeaderboard } from './pages/reports/ScanPerformanceList/ScanPerformanceLeaderboard/ScanPerformanceLeaderboard';
import { ScanPerformanceByTimeList } from './pages/reports/ScanPerformanceByTimeList/ScanPerformanceByTime';
import { ScanPerformanceByTimePerStoreList } from './pages/reports/ScanPerformanceByTimeList/ScanPerformanceByTimePerStoreList';
import { LineHaulScanOverView } from './pages/reports/ScanPerformanceList/LineHaulScanOverView/LineHaulScanOverView';
import HubScanner from './pages/hub/HubScanner';
import {
  GeoZonePreSortScanner,
  RegionZonePreSortScanner,
  ShowBothZonesPreSortScanner,
} from './pages/hub/PreSortScanner';
import InfoScanner from './pages/hub/InfoScanner';
import { WarehouseSystem } from './pages/WarehouseSystem/WarehouseSystem';
import Bundle from './pages/bundle/Bundle';
import Reports from './pages/reports/Reports';
import {
  MyBooking,
  MyBookingMultipleCouriers,
} from './pages/myBooking/MyBooking';
import Users from './pages/users/Users';
import CreateUser from './pages/create-user/CreateUser';
import Accounts from './pages/users/accounts/Accounts';
import AllStoreSettings from './pages/users/storeSettings/AllStoreSettings';
import { Brands } from './pages/users/brands/Brands';
import EditStoreSettings from './pages/users/storeSettings/EditStoreSettings';
import { CapacityManagement } from './pages/Dashboard/CapacityManagement/CapacityManagement';
import { InboundConfig } from './pages/inbound/InboundConfig';
import { CourierAreaConfig } from './pages/courierArea/CourierAreaConfig';
import { AddressFix } from './pages/orders/AddressFix/AddressFix';
import HubBundles from './pages/hub-bundles/HubBundles';
import CourierInvoice from './pages/courier-invoice/CourierInvoice';
import { MyDeliveriesReportComposition } from './pages/myDeliveries/MyDeliveriesReportComposition';
import { MyTips } from './pages/myTips/MyTips';
import { CourierRating } from './pages/myRatingNew/CourierRating';
import { MyRating } from './pages/myRating/MyRating';
import CourierOnboarding from './pages/dipps/CourierOnboarding';
import BankAccountOnboarding from './pages/dipps/BankAccountOnboarding';
import NoMatch404 from './pages/no-match-404/NoMatch404';
import SignIn from './pages/authentication/sign-in/SignIn';
import InviteUser from './pages/users/invite/InviteUser';
import { Partitions } from './pages/partitions/Partitions';
import { PreferredAreas } from './pages/preferred-areas/PreferredAreas';
import { RoutingDebugger } from './pages/routing-debugger/RoutingDebugger';

type RouteType = {
  title: string;
  path: string;
  visibleFor?: RoleType[];
  redirect?: string;
  Component?: any;
  region?: Region;
  view?: MonitorView;
  limitedAccess?: boolean;
  limitedCourier?: boolean;
};

type DispatchRouterType = {
  region: Region;
  limitedAccess: boolean;
  roleType: RoleType;
};

export const DispatchRouter: FC<DispatchRouterType> = ({
  region,
  limitedAccess,
  roleType,
}): JSX.Element => {
  const limitedCourier =
    roleType === RoleType.COURIER && region !== Region.OSLO;

  const routes: RouteType[] = [
    {
      title: 'Sign in',
      path: '/sign-in',
      Component: SignIn,
      limitedAccess: true,
    },
    {
      title: 'Dashboard',
      path: '/',
      redirect: '/dashboard',
      visibleFor: [RoleType.ADMIN],
      limitedAccess,
    },
    {
      title: 'Dashboard',
      path: '/dashboard',
      Component: Dashboard,
      region,
      visibleFor: [RoleType.ADMIN],
      limitedAccess,
    },
    {
      title: 'New order',
      path: '/new-order',
      Component: NewOrder,
      visibleFor: [RoleType.ADMIN],
    },
    {
      title: 'Orders',
      path: '/orders',
      Component: Orders,
      region,
      visibleFor: [RoleType.ADMIN],
    },
    {
      title: 'Orders search',
      path: '/orders/search',
      Component: OrdersSearch,
      visibleFor: [RoleType.ADMIN],
    },
    {
      title: 'Order id',
      path: '/orders/:id',
      visibleFor: [RoleType.ADMIN],
      Component: OrderComponent,
      region,
      limitedAccess,
    },
    {
      title: 'Delivery id',
      path: '/deliveries/:id',
      visibleFor: [RoleType.ADMIN],
      Component: DeliveryComponent,
    },
    {
      title: 'Journey',
      path: '/journey',
      visibleFor: [RoleType.ADMIN],
      Component: JourneysPage,
    },
    {
      title: 'Journey id',
      path: '/journey/:orderId',
      visibleFor: [RoleType.ADMIN],
      Component: JourneysPage,
    },
    {
      title: 'Shift planner',
      path: '/shift-planner',
      visibleFor: [RoleType.ADMIN],
      Component: ShiftPlanner,
      region,
      limitedAccess,
    },
    {
      title: 'Planner',
      path: '/planner',
      visibleFor: [RoleType.ADMIN],
      Component: Planner,
      region,
      limitedAccess,
    },
    {
      title: 'Planner map',
      path: '/planner-map',
      visibleFor: [RoleType.ADMIN],
      redirect: '/planner',
    },
    {
      title: 'Planner print list',
      path: '/planner/print-list',
      visibleFor: [RoleType.ADMIN],
      Component: PlannerPrintList,
    },
    {
      title: 'Old planner',
      path: '/old/planner',
      visibleFor: [RoleType.ADMIN],
      Component: PlannerMap,
      region,
    },
    {
      title: 'Routes',
      path: '/routes',
      visibleFor: [RoleType.ADMIN],
      redirect: '/planner/routes',
    },
    {
      title: 'Routes',
      path: '/planner/routes',
      visibleFor: [RoleType.ADMIN],
      Component: Routes,
      region,
    },
    {
      title: 'Partitions',
      path: '/partitions',
      visibleFor: [RoleType.ADMIN],
      redirect: '/planner/partitions',
    },
    {
      title: 'Partitions',
      path: '/planner/partitions/:date?',
      visibleFor: [RoleType.ADMIN],
      Component: Partitions,
      region,
    },
    {
      title: 'Preferred Areas',
      path: '/preferred-areas',
      visibleFor: [RoleType.ADMIN],
      redirect: '/planner/preferred-areas',
    },
    {
      title: 'Preferred Areas',
      path: '/planner/preferred-areas/:courierId?',
      visibleFor: [RoleType.ADMIN],
      Component: PreferredAreas,
      region,
    },
    {
      title: 'Routing Debugger',
      path: '/planner/routing-debugger/:partition?',
      visibleFor: [RoleType.ADMIN],
      Component: RoutingDebugger,
      region,
    },
    {
      title: 'Monitor',
      path: '/monitor',
      visibleFor: [RoleType.ADMIN],
      Component: Monitor,
      region,
      view: MonitorView.MONITOR,
      limitedAccess,
    },
    {
      title: 'Monitor old',
      path: '/monitor-old',
      visibleFor: [RoleType.ADMIN],
      Component: Monitor,
      region,
      view: MonitorView.MONITOROLD,
    },
    {
      title: 'Monitor completed',
      path: '/monitor/completed',
      visibleFor: [RoleType.ADMIN],
      Component: Monitor,
      region,
      view: MonitorView.COMPLETED,
      limitedAccess,
    },
    {
      title: 'Monitor line haul',
      path: '/monitor/line-haul',
      visibleFor: [RoleType.ADMIN],
      Component: Monitor,
      region,
      view: MonitorView.LINEHAUL,
    },
    {
      title: 'Monitor leaderboard',
      path: '/monitor/scan-leaderboard',
      visibleFor: [RoleType.ADMIN],
      Component: ScanPerformanceLeaderboard,
      region,
    },
    {
      title: 'Monitor couriers',
      path: '/monitor/couriers',
      visibleFor: [RoleType.ADMIN],
      Component: Monitor,
      region,
      view: MonitorView.COURIERS,
    },
    {
      title: 'Monitor bundles',
      path: '/monitor/bundles',
      visibleFor: [RoleType.ADMIN],
      Component: Monitor,
      region,
      view: MonitorView.BUNDLES,
      limitedAccess,
    },
    {
      title: 'Monitor sort list',
      path: '/monitor/sort-list',
      visibleFor: [RoleType.ADMIN],
      Component: Monitor,
      region,
      view: MonitorView.SORTLIST,
      limitedAccess,
    },
    {
      title: 'Monitor sort list 2',
      path: '/monitor/sort-list2',
      visibleFor: [RoleType.ADMIN],
      Component: Monitor,
      region,
      view: MonitorView.SORTLIST2,
    },
    {
      title: 'Monitor line haul',
      path: '/monitor/sort-list2',
      visibleFor: [RoleType.ADMIN],
      Component: Monitor,
      region,
      view: MonitorView.LINEHAUL,
    },
    {
      title: 'Monitor Relabelling',
      path: '/monitor/relabelling',
      visibleFor: [RoleType.ADMIN],
      Component: Monitor,
      region,
      view: MonitorView.RELABELLING,
    },
    {
      title: 'Monitor scan',
      path: '/monitor/scan',
      visibleFor: [RoleType.ADMIN],
      Component: Monitor,
      region,
      view: MonitorView.SCAN,
    },
    {
      title: 'Monitor scan leaderboard',
      path: '/monitor/scan',
      visibleFor: [RoleType.ADMIN],
      Component: ScanPerformanceLeaderboard,
      region,
    },
    {
      title: 'Monitor scan graphs',
      path: '/monitor/scan-graphs',
      visibleFor: [RoleType.ADMIN],
      Component: ScanPerformanceByTimeList,
      region,
    },
    {
      title: 'Monitor scan graphs per store',
      path: '/monitor/scan-graphs-per-store',
      visibleFor: [RoleType.ADMIN],
      Component: ScanPerformanceByTimePerStoreList,
      region,
    },
    {
      title: 'Monitor scan line haul',
      path: '/monitor/scanLineHaul',
      visibleFor: [RoleType.ADMIN],
      Component: LineHaulScanOverView,
      region,
    },
    {
      title: 'Hub info scanner',
      path: '/hub/info-scanner',
      visibleFor: [RoleType.ADMIN],
      Component: InfoScanner,
    },
    {
      title: 'Warehouse',
      path: '/warehouse',
      visibleFor: [RoleType.ADMIN],
      Component: WarehouseSystem,
    },
    {
      title: 'Bundle id',
      path: '/bundles/:bundleId',
      visibleFor: [RoleType.ADMIN],
      Component: Bundle,
    },
    {
      title: 'Reports',
      path: '/reports',
      visibleFor: [RoleType.ADMIN],
      Component: Reports,
      region,
    },
    {
      title: 'Report csv',
      path: '/reports/csv',
      visibleFor: [RoleType.ADMIN],
      Component: Reports,
    },
    {
      title: 'Report volumes',
      path: '/reports/volumes',
      visibleFor: [RoleType.ADMIN],
      Component: Reports,
    },
    {
      title: 'Report co2',
      path: '/reports/co2',
      visibleFor: [RoleType.ADMIN],
      Component: Reports,
    },
    {
      title: 'Report courier',
      path: '/reports/courier',
      visibleFor: [RoleType.ADMIN],
      Component: Reports,
      region,
      limitedAccess,
    },
    {
      title: 'Report driving',
      path: '/reports/driving',
      visibleFor: [RoleType.ADMIN],
      Component: Reports,
    },
    {
      title: 'Report invoice courier',
      path: '/reports/invoiceCourier',
      visibleFor: [RoleType.ADMIN],
      Component: Reports,
      region,
    },
    {
      title: 'Report invoice courier old',
      path: '/reports/invoiceCourierOld',
      visibleFor: [RoleType.ADMIN],
      Component: Reports,
      region,
    },
    {
      title: 'Report invoice courier company',
      path: '/reports/invoiceCourierCompany',
      visibleFor: [RoleType.ADMIN],
      Component: Reports,
      region,
    },
    {
      title: 'Report cost',
      path: '/reports/cost',
      visibleFor: [RoleType.ADMIN],
      Component: Reports,
      region,
    },
    {
      title: 'Report prediction',
      path: '/reports/prediction',
      visibleFor: [RoleType.ADMIN],
      Component: Reports,
    },
    {
      title: 'Booking',
      path: '/booking',
      visibleFor: [RoleType.ADMIN],
      Component: MyBookingMultipleCouriers,
      region,
    },
    {
      title: 'Users',
      path: '/users',
      visibleFor: [RoleType.ADMIN],
      Component: Users,
      limitedAccess,
    },
    {
      title: 'Users create',
      path: '/users/create',
      visibleFor: [RoleType.ADMIN],
      Component: CreateUser,
    },
    {
      title: 'Invite users',
      path: '/users/invite',
      visibleFor: [RoleType.ADMIN],
      Component: InviteUser,
    },
    {
      title: 'Users accounts',
      path: '/users/accounts',
      visibleFor: [RoleType.ADMIN],
      Component: Accounts,
    },
    {
      title: 'Users store settings',
      path: '/users/storesettings',
      visibleFor: [RoleType.ADMIN],
      Component: AllStoreSettings,
    },
    {
      title: 'Brand management',
      path: '/users/brands',
      visibleFor: [RoleType.ADMIN],
      Component: Brands,
    },
    {
      title: 'Users store settings edit',
      path: '/users/storesettings/editstore/:accountId',
      visibleFor: [RoleType.ADMIN],
      Component: EditStoreSettings,
    },
    {
      title: 'Dashboard capacity management',
      path: '/dashboard/capacity-management',
      visibleFor: [RoleType.ADMIN],
      Component: CapacityManagement,
    },
    {
      title: 'Dashboard inbound info',
      path: '/dashboard/inbound-info',
      visibleFor: [RoleType.ADMIN],
      Component: InboundConfig,
    },
    {
      title: 'Algo courier info',
      path: '/algo/courier-info',
      visibleFor: [RoleType.ADMIN],
      Component: CourierAreaConfig,
    },
    {
      title: 'Tools address fix',
      path: '/tools/address-fix',
      visibleFor: [RoleType.ADMIN],
      Component: AddressFix,
      region,
    },
    {
      title: 'My booking',
      path: '/my-booking',
      visibleFor: [RoleType.ADMIN],
      Component: MyBooking,
      region,
    },
    {
      title: 'Monitor hub bundles',
      path: '/monitor/hub-bundles',
      visibleFor: [RoleType.ADMIN],
      Component: HubBundles,
      region,
    },
    {
      title: 'My shift',
      path: '/my-shift',
      visibleFor: [RoleType.ADMIN, RoleType.COURIER],
      Component: ShiftForCourier,
      limitedCourier,
      region,
    },
    {
      title: 'Hub',
      path: '/hub',
      visibleFor: [RoleType.ADMIN, RoleType.COURIER],
      Component: HubScanner,
      region,
      limitedCourier,
      limitedAccess,
    },
    {
      title: 'Hub reg pre sort',
      path: '/hub/reg-pre-sort',
      visibleFor: [RoleType.ADMIN, RoleType.COURIER],
      Component: RegionZonePreSortScanner,
      region,
    },
    {
      title: 'Hub geo pre sort',
      path: '/hub/geo-pre-sort',
      visibleFor: [RoleType.ADMIN, RoleType.COURIER],
      Component: GeoZonePreSortScanner,
      region,
    },
    {
      title: 'Hub pre sort',
      path: '/hub/pre-sort',
      visibleFor: [RoleType.ADMIN, RoleType.COURIER],
      Component: ShowBothZonesPreSortScanner,
      region,
    },
    {
      title: 'My deliveries',
      path: '/my-deliveries',
      Component: CourierInvoice,
      visibleFor: [RoleType.COURIER],
      region,
      limitedCourier,
    },
    {
      title: 'My deliveries old',
      path: '/my-deliveries-old',
      Component: MyDeliveriesReportComposition,
      visibleFor: [RoleType.COURIER],
      region,
      limitedCourier,
    },
    {
      title: 'My tips',
      path: '/my-tips',
      Component: MyTips,
      visibleFor: [RoleType.COURIER],
      limitedCourier,
    },
    {
      title: 'My rating',
      path: '/my-rating',
      Component: CourierRating,
      visibleFor: [RoleType.COURIER],
    },
    {
      title: 'My rating old',
      path: '/my-rating-old',
      Component: MyRating,
      visibleFor: [RoleType.COURIER],
    },
    {
      title: 'Onboarding',
      path: '/onboarding',
      Component: CourierOnboarding,
      visibleFor: [RoleType.COURIER],
    },
    {
      title: 'Onboarding stage 2',
      path: '/onboarding-stage-2',
      Component: BankAccountOnboarding,
      visibleFor: [RoleType.COURIER],
    },
    {
      title: 'My shift',
      path: '/',
      redirect: '/my-shift',
      visibleFor: [RoleType.COURIER],
    },
  ];

  return (
    <Switch>
      {routes
        .filter(route => {
          const isVisibleForRole = route.visibleFor?.includes(roleType);

          if (!isVisibleForRole) {
            return false;
          }

          if (limitedAccess && !route.limitedAccess) {
            return false;
          }

          if (limitedCourier && !route.limitedCourier) {
            return false;
          }

          return true;
        })
        .map(({ path, redirect, Component, view, region, limitedAccess }) => (
          <Route
            key={path}
            exact
            path={path}
            render={props =>
              !!redirect ? (
                <Redirect to={redirect} />
              ) : (
                <Component
                  view={view}
                  region={region}
                  limitedAccess={limitedAccess}
                  {...props}
                />
              )
            }
          />
        ))}
      <Route component={NoMatch404} />
    </Switch>
  );
};
